import * as React from 'react';

import { KeyboardAvoidingView, StyleSheet } from 'react-native';
import { PubSubService } from '@nakedplatform/support';

import { Colors } from 'theme/colors';
import { FontSize } from 'theme/fonts';
import { makeHitSlop } from 'theme/sizes';
import { NakedIcon, StatusIcon } from 'theme/icons';
import { Row } from 'theme/layout';
import { Regular, Medium } from 'theme/text';

import { MODAL_ROUTES } from './routes';
import { Modal } from './box';
import { ModalHeader } from './box/header';
import * as Animations from './box/animations';
// TODO: Fix these type definitions
import { ModalTypes } from './box/types';

import {
  Button,
  CloseButton,
  Description,
  Footer,
  ModalContent,
  ModalInner,
  Title,
  ButtonDivider,
  BackButton,
} from './styles';

const Hub = PubSubService.getInstance();
const ANIMATION_IN_TIMING = 300;
const ANIMATION_OUT_TIMING = 500;
const CLOSE_ICON_SIZE = 12;
const HIT_SLOP = 40;
const DEFAULT_STATE: ModalTypes.State = {
  accentColor: Colors.green,
  animationIn: Animations.animationIn,
  animationOut: Animations.animationOut,
  animationInTiming: ANIMATION_IN_TIMING,
  animationOutTiming: ANIMATION_OUT_TIMING,
  alertType: undefined,
  cancelText: undefined,
  circleColor: undefined,
  circleWidth: undefined,
  circleHeight: undefined,
  content: undefined,
  description: undefined,
  disabled: false,
  iconColor: Colors.white,
  footerText: undefined,
  header: undefined,
  headerContent: undefined,
  hideCloseButton: false,
  isVisible: false,
  backdropOpacity: 1,
  isBackdropClickable: true,
  mode: 'content',
  onBack: undefined,
  onCancel: undefined,
  onClose: undefined,
  onHide: undefined,
  onSubmit: undefined,
  presentationStyle: undefined,
  routeName: undefined,
  statusIcon: undefined,
  styleProps: undefined,
  submitText: undefined,
  title: undefined,
};
const BACK_ICON_SIZE = 15;

const styles = StyleSheet.create({
  modal: {
    ...StyleSheet.absoluteFillObject,
    margin: 0,
    padding: 0,
  },
});

/**
 * Dynamic modal component.
 *
 */
export class ModalBox extends React.PureComponent<Record<string, unknown>, ModalTypes.State> {
  /**
   * Default Header component for a modal box
   *
   */
  public static Header = ModalHeader;
  /**
   * Default Description component for a modal box.
   *
   */
  public static Description = Description;

  /**
   * Default Title component for a modal box.
   *
   */
  public static Title = Title;

  /**
   * Hides the modal.
   *
   * @param onCompleted - Event handler that fires after modal is hidden.
   */
  public static hide(onCompleted?: Callback) {
    Hub.dispatch('modal', {
      event: 'hide',
      data: { onCompleted },
    });
  }

  /**
   * Updates the current modal view without changing the open / close state.
   *
   * @param routeName - The name of the modal route.
   * @param config - Configuration to apply.
   * @param onCompleted - Event handler that fires after modal has updated.
   */
  public static update(
    routeName: ModalTypes.Route,
    config: ModalTypes.Config,
    onCompleted?: Callback
  ) {
    Hub.dispatch('modal', {
      event: 'update',
      data: { routeName, config, onCompleted },
    });
  }

  /**
   * Shows the modal.
   *
   * @param routeName - Name of route to load a particular modal.
   * @param config - Custom configuration applied to modal.
   * @param onCompleted - Event handler that fires after modal is shown.
   */
  public static show(
    routeName: ModalTypes.Route,
    config?: ModalTypes.Config,
    onCompleted?: Callback
  ) {
    Hub.dispatch('modal', {
      event: 'show',
      data: {
        routeName,
        config,
        onCompleted,
      },
    });
  }

  public constructor(props: Record<string, unknown>) {
    super(props);
    this.state = {
      ...DEFAULT_STATE,
    };
    Hub.listen('modal', this.listener.bind(this));
  }

  public componentWillUnmount() {
    Hub.remove('modal', this.listener);
  }

  public render() {
    const {
      animationIn,
      animationOut,
      animationInTiming,
      animationOutTiming,
      accentColor,
      cancelText,
      disabled,
      footerText,
      header,
      hideCloseButton,
      isVisible,
      backdropOpacity,
      isBackdropClickable,
      mode,
      onBack,
      presentationStyle,
      submitText,
    } = this.state;
    const buttonBackgroundColor = disabled ? Colors.alto : accentColor;
    const onBackgroundClick = isBackdropClickable ? this.onCancel : undefined;

    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <Modal
        presentationStyle={presentationStyle}
        animationIn={animationIn}
        animationOut={animationOut}
        animationInTiming={animationInTiming}
        animationOutTiming={animationOutTiming}
        backdropColor={Colors.tundora}
        backdropOpacity={backdropOpacity}
        isVisible={isVisible}
        useNativeDriver
        hideModalContentWhileAnimating
        onBackdropPress={onBackgroundClick}
        style={styles.modal}
        onModalHide={this.onHide}>
        {mode === 'content' ? (
          <KeyboardAvoidingView behavior="padding">
            <ModalContent>
              {header}
              <ModalInner>
                {onBack && (
                  <BackButton onPress={onBack}>
                    <NakedIcon name="arrow-left" color={Colors.black} size={BACK_ICON_SIZE} />
                  </BackButton>
                )}
                {!hideCloseButton && (
                  <CloseButton onPress={this.onClose} hitSlop={makeHitSlop(HIT_SLOP)}>
                    <NakedIcon name="close" size={CLOSE_ICON_SIZE} color={Colors.black} />
                  </CloseButton>
                )}
                {this.renderContent()}
              </ModalInner>
              {footerText && (
                <Footer>
                  <Regular color={Colors.black} fontSize={FontSize.Medium} textAlign="center">
                    {footerText}
                  </Regular>
                </Footer>
              )}
              <Row>
                {cancelText && (
                  <Button
                    backgroundColor={buttonBackgroundColor}
                    disabled={disabled}
                    onPress={this.onCancel}>
                    <Medium fontSize={FontSize.Medium} color={Colors.white}>
                      {cancelText || 'Cancel'}
                    </Medium>
                  </Button>
                )}
                {cancelText && submitText && <ButtonDivider />}
                {submitText && (
                  <Button
                    backgroundColor={buttonBackgroundColor}
                    disabled={disabled}
                    onPress={this.onSubmit}>
                    <Medium fontSize={FontSize.Medium} color={Colors.white}>
                      {submitText || 'Confirm'}
                    </Medium>
                  </Button>
                )}
              </Row>
            </ModalContent>
          </KeyboardAvoidingView>
        ) : (
          this.renderContent()
        )}
      </Modal>
    );
  }

  /**
   * Hides the modal.
   *
   * @param onCompleted - Event handler that fires after modal is hidden.
   */
  public readonly hide = (onCompleted?: Callback) => {
    const { state } = this;

    this.setState({ ...state, isVisible: false }, onCompleted);
  };

  /**
   * An event listener for handling requests to modal changes.
   *
   * @param eventData - Event payload information.
   */
  public listener(eventData: EventCapsule) {
    const { payload } = eventData;

    if (!payload || !payload.data) {
      return;
    }

    const { event, data } = payload;
    const { routeName, config, onCompleted } = data;

    switch (event) {
      case 'hide':
        this.hide(onCompleted);
        break;
      case 'show':
        this.show(routeName, config, onCompleted);
        break;
      case 'update':
        this.update(routeName, config, onCompleted);
        break;
      default:
        break;
    }
  }

  /**
   * Display the modal (Does nothing if the screen is already open).
   *
   * @param routeName - Name of route to load a particular modal.
   * @param config - Custom configuration applied to modal.
   * @param onCompleted - Event handler that fires after modal is shown.
   */
  public readonly show = (
    routeName: ModalTypes.Route,
    config?: ModalTypes.Config,
    onCompleted?: Callback
  ) => {
    const routeConfig = MODAL_ROUTES[routeName];

    if (!routeConfig) {
      throw new Error(`[ModalBox]: Route "${routeName}" does not exist.`);
    }

    this.setState(
      () => ({
        ...DEFAULT_STATE,
        ...routeConfig,
        ...config,
        isVisible: true,
        routeName,
      }),
      onCompleted
    );
  };

  /**
   * Updates the current modal view without changing the open / close state.
   *
   * @param routeToUpdate - The name of the modal route.
   * @param config - Configuration to apply.
   * @param onCompleted - Event handler that fires after modal has updated.
   */
  public readonly update = (
    routeToUpdate: ModalTypes.Route,
    config: ModalTypes.Config,
    onCompleted?: Callback
  ) => {
    const { routeName } = this.state;

    if (routeToUpdate !== routeName) {
      return;
    }

    this.setState(() => config, onCompleted);
  };

  /**
   * Hides the modal and emits an `onCancel` action.
   *
   */
  private readonly onCancel = () => {
    const { onCancel } = this.state;

    if (!onCancel || !onCancel()) {
      this.hide();
    }
  };

  /**
   * Closes the modal and emits an `onClose` event.
   *
   */
  private readonly onClose = () => {
    const { onClose } = this.state;

    if (!onClose || !onClose()) {
      this.hide();
    }
  };

  /**
   * Hides the modal and emits an `onHide` event.
   */
  private readonly onHide = () => {
    const { onHide } = this.state;

    if (!onHide || !onHide()) {
      this.hide();
    }
  };

  /**
   * Hides the modal and emits an `onSubmit` event.
   *
   */
  private readonly onSubmit = () => {
    const { onSubmit } = this.state;

    if (!onSubmit || !onSubmit()) {
      this.hide();
    }
  };

  /**
   * Renders the content of the modal.
   *
   */
  private readonly renderContent = () => {
    const {
      accentColor,
      alertType,
      content,
      headerContent,
      statusIcon,
      styleProps,
      title,
      description,
      iconColor,
      circleColor,
      circleWidth,
      circleHeight,
    } = this.state;

    const displayTitle =
      styleProps && styleProps.title && title ? (
        <Title style={{ ...styleProps.title } as never}>{title}</Title>
      ) : (
        <Title>{title}</Title>
      );

    return (
      <>
        {alertType || statusIcon ? (
          <StatusIcon
            alertType={alertType}
            accentColor={accentColor}
            icon={statusIcon}
            iconColor={iconColor}
            circleColor={circleColor}
            circleWidth={circleWidth}
            circleHeight={circleHeight}
          />
        ) : undefined}
        {headerContent}
        {displayTitle}
        {description && (
          <Description textAlign="center" {...(styleProps?.description as never)}>
            {description}
          </Description>
        )}
        {content}
      </>
    );
  };
}

// Type definitions

interface EventCapsule {
  /** Payload containing details of the modal change event  */
  payload: EventPayload;
}

interface EventPayload {
  /** Name of the change event to fire */
  event: string;
  /** Properties on the modal to change */
  data?: ModalProperties;
}

interface ModalProperties {
  /** Configuration options for the modal */
  config: ModalTypes.Config;
  /** Event that fires once an object has completed an operation */
  onCompleted: Callback;
  /** Name of the modal to update */
  routeName: ModalTypes.Route;
}
